/**
 * LearningArc - Visualizes the full K-12 progression for a scenario
 * 
 * Shows parents: "Here's how this ONE game story teaches math
 * from elementary through calculus"
 */

import { useState } from 'react';
import {
  SCENARIOS,
  LEVEL_INFO,
  getLearningArc,
  getScenarioProgress,
  type MathLevel,
  type QuestionLens,
} from '../lib/scenarios';

interface LearningArcProps {
  scenarioId: string;
  completedQuestions?: Array<{ lens: QuestionLens; level: MathLevel; score: number }>;
  childName?: string;
}

export function LearningArc({ scenarioId, completedQuestions = [], childName }: LearningArcProps) {
  const [expandedLevel, setExpandedLevel] = useState<MathLevel | null>(null);
  
  const scenario = SCENARIOS[scenarioId];
  const arc = getLearningArc(scenarioId);
  const progress = getScenarioProgress(scenarioId, completedQuestions);
  
  if (!scenario) return null;
  
  return (
    <div className="rounded-2xl bg-blox-card border border-blox-surface p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-4xl">{scenario.theme.emoji}</span>
            <div>
              <h2 className="font-display text-xl text-white">{scenario.name}</h2>
              <p className="text-sm text-slate-400">Learning Arc</p>
            </div>
          </div>
          <p className="text-sm text-slate-300 max-w-md">{scenario.description}</p>
        </div>
        
        {/* Progress ring */}
        <div className="relative w-16 h-16">
          <svg className="w-full h-full -rotate-90">
            <circle
              cx="32" cy="32" r="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              className="text-blox-surface"
            />
            <circle
              cx="32" cy="32" r="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeDasharray={`${progress.progressPercent * 1.76} 176`}
              strokeLinecap="round"
              className="text-blox-accent"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-semibold text-white">{progress.progressPercent}%</span>
          </div>
        </div>
      </div>
      
      {/* Current level badge */}
      <div className="mb-6 p-3 rounded-xl bg-blox-accent/10 border border-blox-accent/30">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-blox-accent uppercase tracking-wider">
              {childName ? `${childName}'s` : 'Current'} Level
            </p>
            <p className="text-lg font-display text-white">
              {LEVEL_INFO[progress.currentLevel].name}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">Grade Range</p>
            <p className="text-sm text-slate-300">{LEVEL_INFO[progress.currentLevel].gradeRange}</p>
          </div>
        </div>
      </div>
      
      {/* The formula */}
      <div className="mb-6 p-4 rounded-xl bg-blox-dark font-mono text-center">
        <p className="text-xs text-slate-500 mb-1">Same Story, Different Lenses</p>
        <p className="text-lg text-blox-xp">{scenario.baseFunction}</p>
      </div>
      
      {/* Level progression */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
          📚 Full Progression
        </h3>
        
        {arc.map(({ level, levelInfo, availableLenses }, index) => {
          const isCurrentLevel = level === progress.currentLevel;
          const isPastLevel = arc.findIndex(a => a.level === progress.currentLevel) > index;
          const isExpanded = expandedLevel === level;
          
          return (
            <div key={level} className="relative">
              {/* Connector line */}
              {index < arc.length - 1 && (
                <div className={`absolute left-4 top-10 w-0.5 h-6 ${
                  isPastLevel ? 'bg-blox-accent' : 'bg-blox-surface'
                }`} />
              )}
              
              <button
                onClick={() => setExpandedLevel(isExpanded ? null : level)}
                className={`w-full text-left rounded-xl p-4 transition-all ${
                  isCurrentLevel 
                    ? 'bg-blox-accent/10 border border-blox-accent/30' 
                    : isPastLevel
                    ? 'bg-blox-surface/50 border border-blox-xp/30'
                    : 'bg-blox-surface/30 border border-blox-surface hover:border-slate-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Status indicator */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    isPastLevel 
                      ? 'bg-blox-xp text-blox-dark' 
                      : isCurrentLevel 
                      ? 'bg-blox-accent text-blox-dark'
                      : 'bg-blox-surface text-slate-400'
                  }`}>
                    {isPastLevel ? '✓' : index + 1}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white">{levelInfo.name}</span>
                      <span className="text-xs text-slate-500">({levelInfo.gradeRange})</span>
                      {isCurrentLevel && (
                        <span className="px-2 py-0.5 rounded text-xs bg-blox-accent text-blox-dark font-semibold">
                          NOW
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-400">{levelInfo.description}</p>
                  </div>
                  
                  {/* Lens count */}
                  <div className="text-right">
                    <p className="text-sm font-mono text-slate-300">
                      {availableLenses.length} lens{availableLenses.length !== 1 ? 'es' : ''}
                    </p>
                    <p className="text-xs text-slate-500">
                      {levelInfo.xpMultiplier}x XP
                    </p>
                  </div>
                  
                  {/* Expand icon */}
                  <svg 
                    className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {/* Expanded lenses */}
              {isExpanded && (
                <div className="mt-2 ml-11 p-4 rounded-xl bg-blox-dark border border-blox-surface animate-slide-up">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">
                    Question Types at This Level
                  </p>
                  <div className="space-y-2">
                    {availableLenses.map(({ lens, config }) => {
                      const isMastered = progress.masteredLenses.includes(lens);
                      const isNext = progress.nextLenses.includes(lens);
                      
                      return (
                        <div 
                          key={lens}
                          className={`p-3 rounded-lg ${
                            isMastered 
                              ? 'bg-blox-xp/10 border border-blox-xp/30'
                              : isNext
                              ? 'bg-blox-accent/5 border border-blox-accent/20'
                              : 'bg-blox-surface/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-white">{config.name}</span>
                            {isMastered && <span className="text-blox-xp text-sm">✓ Mastered</span>}
                            {isNext && <span className="text-blox-accent text-xs">Up Next</span>}
                          </div>
                          <p className="text-xs text-slate-400">{config.description}</p>
                          <p className="text-xs text-slate-500 mt-1 italic">
                            "{config.questionTemplates[0].replace(/{[^}]+}/g, '...')}"
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Key insight for parents */}
      <div className="mt-6 p-4 rounded-xl bg-blox-surface/50 border border-slate-700">
        <p className="text-sm text-slate-300">
          <span className="text-blox-accent font-semibold">💡 Key Insight:</span>{' '}
          Your child uses the <span className="text-white">same story</span> from elementary through calculus. 
          Only the <span className="text-white">question type</span> changes — building deep understanding 
          without constant context-switching.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// SCENARIO SELECTOR COMPONENT
// ============================================================================

interface ScenarioSelectorProps {
  onSelect: (scenarioId: string) => void;
  selectedId?: string;
}

export function ScenarioSelector({ onSelect, selectedId }: ScenarioSelectorProps) {
  const scenarios = Object.values(SCENARIOS);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {scenarios.map(scenario => (
        <button
          key={scenario.id}
          onClick={() => onSelect(scenario.id)}
          className={`text-left p-4 rounded-2xl border transition-all hover:scale-[1.02] ${
            selectedId === scenario.id
              ? 'bg-blox-accent/10 border-blox-accent'
              : 'bg-blox-card border-blox-surface hover:border-blox-accent/50'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">{scenario.theme.emoji}</span>
            <div>
              <h3 className="font-display text-lg text-white">{scenario.name}</h3>
              <p className="text-xs text-slate-400">{scenario.context}</p>
            </div>
          </div>
          <p className="text-sm text-slate-300 mb-3">{scenario.description}</p>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">
              {scenario.availableLenses.length} question types
            </span>
            <span className="font-mono text-blox-accent">
              {scenario.simplifiedFunction || scenario.baseFunction}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// LENS LADDER COMPONENT (shows progression within a scenario)
// ============================================================================

interface LensLadderProps {
  scenarioId: string;
  currentLevel: MathLevel;
  onSelectLens: (lens: QuestionLens) => void;
  masteredLenses?: QuestionLens[];
}

export function LensLadder({ 
  scenarioId, 
  currentLevel, 
  onSelectLens,
  masteredLenses = [] 
}: LensLadderProps) {
  const scenario = SCENARIOS[scenarioId];
  if (!scenario) return null;
  
  const arc = getLearningArc(scenarioId);
  const currentArc = arc.find(a => a.level === currentLevel);
  
  if (!currentArc) return null;
  
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
        {scenario.theme.emoji} {LEVEL_INFO[currentLevel].name} Quests
      </h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {currentArc.availableLenses.map(({ lens, config }) => {
          const isMastered = masteredLenses.includes(lens);
          
          return (
            <button
              key={lens}
              onClick={() => onSelectLens(lens)}
              disabled={isMastered}
              className={`p-3 rounded-xl text-left transition-all ${
                isMastered
                  ? 'bg-blox-xp/10 border border-blox-xp/30 opacity-60'
                  : 'bg-blox-surface border border-blox-card hover:border-blox-accent'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-white text-sm">{config.name}</span>
                {isMastered && <span className="text-blox-xp">✓</span>}
              </div>
              <p className="text-xs text-slate-400">{config.conceptualGoal}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
